import React, { useEffect, useState } from 'react'
import { Badge, Box, Button, Group, Text, TextInput } from '@mantine/core'
import { useStore } from '../../stores/store'
import agent from '../../api/agent'
import type { TagDto } from '../../models/tag'
import { TagStatus } from '../../models/tag'

interface TagPickerProps {
  targetType: number;
  targetId: string;
  currentTags: TagDto[];
  onTagApplied: (tag: TagDto) => void;
  onTagRemoved: (tag: TagDto) => void;
  maxTags?: number;
  readOnly?: boolean;
  aiContext?: { name: string; description?: string };
}

export default function TagPicker ({
  targetType,
  targetId,
  currentTags,
  onTagApplied,
  onTagRemoved,
  maxTags = 10,
  readOnly = false,
  aiContext
}: TagPickerProps) {
  const { tagStore } = useStore()
  const [search, setSearch] = useState('')
  const [suggestions, setSuggestions] = useState<TagDto[]>([])
  const [loading, setLoading] = useState(false)
  const [aiFilling, setAiFilling] = useState(false)

  useEffect(() => {
    if (!search.trim()) {
      setSuggestions([])
      return
    }
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const list = await agent.Tags.list({ q: search.trim() })
        const active = (list ?? []).filter((tag) => tag.status === TagStatus.Active)
        setSuggestions(active)
      } catch {
        setSuggestions([])
      } finally {
        setLoading(false)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  const appliedIds = new Set(currentTags.map((t) => t.id))
  const selectable = suggestions.filter((s) => !appliedIds.has(s.id))
  const atMax = currentTags.length >= maxTags

  const handleSelect = async (tag: TagDto) => {
    if (atMax) return
    try {
      await tagStore.applyTag(tag.id, targetType, targetId)
      onTagApplied(tag)
      setSearch('')
      setSuggestions([])
    } catch {
      // error handled by interceptor
    }
  }

  const handleRemove = async (tag: TagDto) => {
    if (readOnly) return
    try {
      await tagStore.removeTag(tag.id, targetType, targetId)
      onTagRemoved(tag)
    } catch {
      // error handled by interceptor
    }
  }

  const handleAiSuggest = async () => {
    if (!aiContext || aiFilling) return
    setAiFilling(true)
    try {
      const result = await agent.AiAssist.tagFill(targetType, aiContext.name, aiContext.description)
      const currentAppliedIds = new Set(currentTags.map((t) => t.id))
      for (const tag of result.tags ?? []) {
        if (currentAppliedIds.has(tag.id)) continue
        if (currentAppliedIds.size >= maxTags) break
        try {
          await tagStore.applyTag(tag.id, targetType, targetId)
          onTagApplied(tag)
          currentAppliedIds.add(tag.id)
        } catch {
          // skip failed tag
        }
      }
    } catch {
      // silent
    } finally {
      setAiFilling(false)
    }
  }

  return (
    <Box>
      <Group position="apart" mb="xs">
        <Text size="sm" weight={500}>
          Tags
        </Text>
        {!readOnly && aiContext && (
          <Button
            size="xs"
            variant="subtle"
            compact
            loading={aiFilling}
            disabled={atMax || !aiContext.name?.trim()}
            onClick={handleAiSuggest}
          >
            AI suggest
          </Button>
        )}
      </Group>
      <Group spacing="xs" mb="sm">
        {(currentTags ?? []).map((tag) => (
          <Badge
            key={tag.id}
            size="sm"
            variant="light"
            rightSection={
              !readOnly
                ? (
                <span
                  style={{ cursor: 'pointer', marginLeft: 4 }}
                  onClick={() => handleRemove(tag)}
                  role="button"
                  aria-label={`Remove ${tag.displayName}`}
                >
                  ×
                </span>
                  )
                : null
            }
          >
            {tag.displayName || tag.name}
          </Badge>
        ))}
      </Group>
      {!readOnly && (
        <>
          {atMax
            ? (
            <Text size="sm" color="dimmed">
              Max tags reached ({maxTags}).
            </Text>
              )
            : (
            <>
              <TextInput
                placeholder="Type to search tags..."
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                onBlur={() => setTimeout(() => setSuggestions([]), 200)}
              />
              {selectable.length > 0 && (
              <Box
                component="ul"
                sx={{ margin: 0, padding: 0, listStyle: 'none', border: '1px solid var(--mantine-color-gray-3)', borderRadius: 4, marginTop: 2, maxHeight: 200, overflow: 'auto' }}
              >
                {selectable.map((t) => (
                  <li
                    key={t.id}
                    style={{ padding: '8px 12px', cursor: 'pointer' }}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(t) }}
                  >
                    {t.displayName || t.name}
                  </li>
                ))}
              </Box>
              )}
            </>
              )}
        </>
      )}
    </Box>
  )
}
